import React, { useEffect, useState, useMemo } from "react";
import { put } from "../lib/http.js";
import { API, BASE_URL } from "../config/api.js";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext.jsx";
import { toast } from "react-toastify";
import { absMediaUrl } from "../api/events.js";
import {
  fetchUserProfile,
  getStoredAuthUser,
  getStoredToken,
  getStoredUserId,
  normalizeProfileResponse,
} from "../lib/userProfile.js";

const getInitials = (nameOrPhone = "") =>
  String(nameOrPhone)
    .trim()
    .split(/\s+/)
    .map((n) => n[0]?.toUpperCase() || "")
    .join("")
    .slice(0, 2);

const isoToInputDate = (iso) => {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    const mm = `${d.getMonth() + 1}`.padStart(2, "0");
    const dd = `${d.getDate()}`.padStart(2, "0");
    return `${d.getFullYear()}-${mm}-${dd}`;
  } catch {
    return "";
  }
};

const UPLOAD_FIELD_NAME = "file";

const inputClass =
  "mt-1 block w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-900 shadow-sm outline-none transition placeholder:text-slate-400 focus:border-amber-400 focus:ring-2 focus:ring-amber-200/80";

export default function ProfilePage() {
  const { updateUser, logout } = useAuth?.() ?? { updateUser: () => {}, logout: () => {} };
  const navigate = useNavigate();

  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [avatarUploading, setAvatarUploading] = useState(false);

  const [form, setForm] = useState({
    name: "",
    email: "",
    dob: "",
    gender: "",
  });

  const [phone, setPhone] = useState("");
  const [error, setError] = useState("");

  const headerStyle = useMemo(
    () => ({ backgroundSize: "200% 200%", animation: "goldShift 10s ease infinite" }),
    []
  );

  const avatarSrc = useMemo(() => {
    const raw = user?.avatar || user?.profileImage;
    if (!raw) return "";
    return absMediaUrl(raw) || raw;
  }, [user]);

  useEffect(() => {
    const fetchUser = async () => {
      const stored = getStoredAuthUser();
      const token = getStoredToken(stored);
      const userId = getStoredUserId(stored);

      if (!token || !userId) {
        setLoading(false);
        navigate("/login", { replace: true, state: { from: "/profile" } });
        return;
      }

      const profile = await fetchUserProfile(userId, token);
      if (profile) {
        setUser(profile);
        setForm({
          name: profile.name || "",
          email: profile.email || "",
          dob: profile.dob ? isoToInputDate(profile.dob) : "",
          gender: profile.gender || "",
        });
        setPhone(profile.mobile || profile.phone || "");
        setLoading(false);
        return;
      }

      const fallback = normalizeProfileResponse(stored);
      if (fallback?.name || fallback?.mobile || fallback?.email) {
        setUser(fallback);
        setForm({
          name: fallback.name || "",
          email: fallback.email || "",
          dob: fallback.dob ? isoToInputDate(fallback.dob) : "",
          gender: fallback.gender || "",
        });
        setPhone(fallback.mobile || fallback.phone || "");
        setError("Could not refresh profile from server. Showing saved details.");
      } else {
        setError("Failed to load profile.");
        toast.error("Failed to load profile. Please log in again.");
      }
      setLoading(false);
    };

    fetchUser();
  }, [navigate]);

  const handleSaveBasic = async () => {
    try {
      setSaving(true);
      setError("");

      const token = getStoredToken();
      const userId = getStoredUserId();

      if (!token || !userId) {
        toast.error("Please login again.");
        return;
      }

      const payload = {
        name: (form.name || "").trim(),
        email: (form.email || "").trim(),
        dob: form.dob || null,
        gender: form.gender || null,
      };

      const res = await put(`${API.user.updateProfile}/${userId}`, payload, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
          "Content-Type": "application/json",
        },
      });

      if (res) {
        const merged = { ...(user || {}), ...payload };
        setUser(merged);
        updateUser(payload);
        window.dispatchEvent(new Event("profile:updated"));
        toast.success(res?.message || "Profile updated successfully!");
      } else {
        const msg = res?.message || "Failed to update profile.";
        setError(msg);
        toast.error(msg);
      }
    } catch (err) {
      console.error("Error updating profile:", err);
      const msg = err?.message || "Server error. Please try again.";
      setError(msg);
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  const handleAvatarSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      setAvatarUploading(true);

      const parsed = JSON.parse(localStorage.getItem("bb_user") || "null");
      const token = parsed?.token;
      const userId = parsed?.id;
      if (!token || !userId) {
        toast.error("Please login again.");
        return;
      }

      const MAX_MB = 5;
      if (file.size > MAX_MB * 1024 * 1024) {
        toast.error(`Max ${MAX_MB}MB allowed`);
        e.target.value = "";
        return;
      }

      const fd = new FormData();
      fd.append(UPLOAD_FIELD_NAME, file);
      const endpoint = `${BASE_URL}${API.user.userProfile}/${userId}`;

      const resp = await fetch(endpoint, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });

      const text = await resp.text();
      let data;
      try {
        data = text ? JSON.parse(text) : {};
      } catch {
        data = { raw: text };
      }

      if (!resp.ok) {
        const msg = data?.message || data?.error || `Upload failed (HTTP ${resp.status})`;
        throw new Error(msg);
      }

      const newAvatar =
        data?.url ||
        data?.imageUrl ||
        data?.data?.url ||
        data?.data?.imageUrl ||
        data?.profileImage ||
        null;

      if (newAvatar) {
        setUser((u) => ({ ...(u || {}), avatar: newAvatar, profileImage: newAvatar }));
        updateUser({ avatar: newAvatar, profileImage: newAvatar });
        window.dispatchEvent(new Event("profile:updated"));
        toast.success(data?.message || "Profile photo updated!");
      } else {
        toast.success(data?.message || "Uploaded successfully!");
      }
    } catch (err) {
      console.error("Avatar upload error:", err);
      toast.error(err?.message || "Upload failed.");
    } finally {
      setAvatarUploading(false);
      e.target.value = "";
    }
  };

  const handleLogout = () => {
    try {
      logout();
    } catch {
      navigate("/login");
    }
  };

  if (loading) {
    return (
      <div className="grid min-h-[50vh] place-items-center bg-gradient-to-b from-stone-50 via-white to-amber-50/50 font-sans px-4">
        <div className="flex flex-col items-center gap-3">
          <div className="h-9 w-9 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
          <p className="text-sm font-medium text-slate-600">Loading profile…</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="grid min-h-[50vh] place-items-center bg-gradient-to-b from-stone-50 to-white font-sans px-4">
        <p className="rounded-2xl border border-rose-200 bg-rose-50 px-5 py-3 text-sm font-semibold text-rose-700 shadow-sm">
          Failed to load user profile.
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-stone-50 via-white to-amber-50/40 font-sans antialiased">
      <style>{`
        @keyframes goldShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="goldShift"] { animation: none !important; }
        }
      `}</style>

      <div className="mx-auto max-w-3xl px-4 py-8 sm:py-10">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl border border-white/40 shadow-lg shadow-amber-900/10 ring-1 ring-amber-100/80">
          <div
            className="absolute inset-0 bg-gradient-to-br from-amber-500 via-orange-500 to-amber-600"
            style={headerStyle}
          />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.12]"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, rgba(255,255,255,0.9) 1px, transparent 0)",
              backgroundSize: "18px 18px",
            }}
          />
          <div className="relative z-10 flex flex-col items-center px-6 py-8 text-center text-white sm:px-10 sm:py-10">
            <div className="relative">
              {avatarSrc ? (
                <img
                  src={avatarSrc}
                  alt=""
                  className="h-24 w-24 rounded-full object-cover shadow-lg ring-4 ring-white/40 sm:h-28 sm:w-28"
                />
              ) : (
                <div className="grid h-24 w-24 place-items-center rounded-full bg-white/20 text-3xl font-black tracking-tight text-white ring-4 ring-white/35 backdrop-blur-sm sm:h-28 sm:w-28 sm:text-4xl">
                  {getInitials(user.name || user.mobile || "")}
                </div>
              )}
              <label className="absolute -bottom-1 left-1/2 flex -translate-x-1/2 cursor-pointer items-center gap-1.5 rounded-full bg-white px-3 py-1 text-xs font-bold text-amber-900 shadow-md ring-1 ring-black/5 transition hover:bg-amber-50">
                <input type="file" accept="image/*" onChange={handleAvatarSelect} className="hidden" />
                {avatarUploading ? "…" : "Photo"}
              </label>
            </div>

            <h1 className="mt-5 text-2xl font-black tracking-tight drop-shadow-sm sm:text-3xl">
              {user.name || "Your profile"}
            </h1>
            <div className="mt-3 flex flex-wrap items-center justify-center gap-2 text-sm font-medium text-white/95">
              {user.email ? (
                <span className="rounded-full bg-black/15 px-3 py-1 backdrop-blur-sm">{user.email}</span>
              ) : null}
              {user.mobile ? (
                <span className="rounded-full bg-black/15 px-3 py-1 backdrop-blur-sm">{user.mobile}</span>
              ) : null}
            </div>
          </div>
        </div>

        {/* Form */}
        <div className="mt-6 rounded-2xl border border-slate-200/90 bg-white p-5 shadow-md sm:p-7">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-slate-100 pb-4">
            <div>
              <h2 className="text-lg font-bold tracking-tight text-slate-900 sm:text-xl">Edit profile</h2>
              <p className="mt-0.5 text-sm text-slate-500">Update how we show your name and contact you.</p>
            </div>
            {error ? (
              <span className="max-w-full rounded-lg bg-rose-50 px-2.5 py-1 text-xs font-semibold text-rose-700">
                {error}
              </span>
            ) : null}
          </div>

          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <Field
              label="Name"
              hint="Your full name"
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              placeholder="e.g. Rahul Sharma"
            />
            <Field
              label="Email"
              type="email"
              hint="Used for receipts and updates"
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
              placeholder="you@example.com"
            />
            <Field label="Date of birth" type="date" value={form.dob} onChange={(v) => setForm({ ...form, dob: v })} />
            <div>
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Gender</label>
              <select
                name="gender"
                value={form.gender}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
                className={inputClass}
              >
                <option value="">Select</option>
                <option>Male</option>
                <option>Female</option>
                <option>Other</option>
              </select>
            </div>

            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">Phone</label>
              <input
                type="text"
                value={phone}
                readOnly
                disabled
                className={`${inputClass} cursor-not-allowed bg-slate-50 text-slate-600`}
              />
              <p className="mt-1.5 text-xs text-slate-500">Phone is verified at signup and cannot be changed here.</p>
            </div>
          </div>

          <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:items-center">
            <button
              type="button"
              onClick={handleSaveBasic}
              disabled={saving}
              className="inline-flex flex-1 items-center justify-center rounded-xl bg-amber-400 px-5 py-3 text-sm font-bold text-slate-900 shadow-sm transition hover:bg-amber-300 disabled:cursor-wait disabled:opacity-70"
            >
              {saving ? "Saving…" : "Save changes"}
            </button>
            <button
              type="button"
              onClick={handleLogout}
              className="inline-flex flex-1 items-center justify-center rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-bold text-slate-800 shadow-sm transition hover:bg-slate-50"
            >
              Log out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, value, onChange, type = "text", placeholder = "" }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">{label}</label>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={inputClass}
      />
      {hint ? <p className="mt-1.5 text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}
